import React from 'react';
import { useUniqueId } from '../useUniqueId';
import classNames from 'classnames';
import styles from './TextField.module.scss';
/** Drop-in replacement for `TextField` from `wix-ui-tpa/cssVars`. */
export const TextField = React.forwardRef(({ className, error = false, errorMessage, withClearButton = false, onClear, 'data-hook': dataHook, ...rest }, ref) => {
    const id = useUniqueId('text-field');
    /* `this._errorMessageId = uniqueId('error-message')` in TextField.js, linked from the input so a
       screen-reader user who refocuses the invalid field hears WHICH error it has — the `role=alert`
       announcement only fires once, when the message first appears. */
    const errorMessageId = useUniqueId('error-message');
    const hasValue = typeof rest.value === 'string' ? rest.value.length > 0 : rest.value != null;
    /*
     * `CoreInput` keeps focus in React state and writes it on its OWN root as a Stylable state,
     * which is why the sheet's `.input:focus` compiles to the `.focus` CLASS rather than the native
     * pseudo-class. Without emitting it, every `.input.focus` rule is unmatched and a focused field
     * keeps its resting border.
     */
    const [focused, setFocused] = React.useState(false);
    /*
     * IconButton is wrapped in wix-ui-core's `focusableHoc`, which puts Focusable's `focus` state
     * on the button as a CLASS, not via the `:focus` pseudo. `.iconButtonRoot.focusableFocus`
     * carries the 0.7 focus/hover opacity.
     */
    const [clearButtonFocused, setClearButtonFocused] = React.useState(false);
    const { onFocus, onBlur, ...inputProps } = rest;
    return (
    /*
     * THREE levels, as WUT renders them: this root, then ErrorMessageWrapper, then CoreInput
     * (one element carrying both sheets' classes around `input.nativeInput`). No label element:
     * upstream renders one only for the floating-label variant, which is unported.
     *
     * The caller's `className` belongs on THIS root, as upstream puts it
     * (`st(classes.root, stStates, className)`) — contact-details.scss sizes the field through it
     * and targets `[data-part='input']` as a descendant, so on CoreInput both would miss.
     */
    React.createElement("div", { "data-hook": "text-field", 
        /*
         * The root carries WUT's state set, not just `error`. `stStates` in TextField.js is
         * { error, theme, success, disabled, hasPrefix, newErrorMessage, hasSuffix,
         *   hasErrorMessage, hasFloatingLabel, hasFloatingLabelActive, readOnlyCustom }; the ones
         * below are those this port can reach.
         *
         * `newErrorMessage` is load-bearing: WUT's error border lives at
         * `.root.error.newErrorMessage .input` and is `!important`, which is the only way it beats
         * contact-details.scss's own `!important` on `[data-part='input']` (equal importance, three
         * classes against two). Without it an invalid field keeps the ordinary grey border.
         */
        className: classNames(styles.root, 
        /* unconditional: both call sites passed `newErrorMessage`, so the ErrorMessageWrapper
           presentation is the only one this package ships. */
        styles.newErrorMessage, {
            [styles.error]: error,
            [styles.hasErrorMessage]: !!errorMessage,
            [styles.hasSuffix]: withClearButton && hasValue,
            [styles.disabled]: !!rest.disabled,
        }, className) },
        React.createElement("div", { className: classNames(styles.errorWrapperRoot, styles.errorWrapper, {
                [styles.errorWrapperVisible]: error && !!errorMessage,
            }) },
            React.createElement("div", { "data-part": "input", "data-hook": dataHook, className: classNames(styles.coreRoot, styles.input, styles['theme-box'], {
                    /* `error: !!error && !disabled` is upstream's own guard — a disabled field does not
                       also paint as an error. */
                    [styles.error]: error && !rest.disabled,
                    [styles.disabled]: !!rest.disabled,
                    [styles.focus]: focused,
                }) },
                React.createElement(React.Fragment, null,
                    React.createElement("input", { ...inputProps, onFocus: (event) => {
                            setFocused(true);
                            onFocus?.(event);
                        }, onBlur: (event) => {
                            setFocused(false);
                            onBlur?.(event);
                        }, ref: ref, id: id, "data-part": "nativeInput", type: rest.type ?? 'text', "aria-invalid": error || undefined, "aria-describedby": error && errorMessage ? errorMessageId : undefined, 
                        /*
                         * The reserved end padding tracks the BUTTON, not the prop: in WUT the clear
                         * affordance is real layout (`.clearButtonWrapper` plus a 4px `.clearButtonGap` as
                         * flex children), so an empty field reserves nothing and keeps its full content
                         * width. Gating on `withClearButton` alone reserves 36px permanently and clips
                         * placeholders that only just fit.
                         */
                        className: classNames(styles.nativeInput, 'has-custom-focus', {
                            [styles.withClearButton]: withClearButton && hasValue,
                        }), "data-hook": dataHook }),
                    withClearButton && hasValue && (
                    /*
                     * `_getSuffix` -> `_getClearButton` in TextField.js, verbatim:
                     *   div.suffixWrapper > div.clearButtonWrapper > Tooltip > IconButton
                     * and Tooltip is a CorePopover, hence the `.popoverRoot` / `.popoverElement` pair. The
                     * button is an IconButton (skin Line, theme None), so it renders CoreButtonNext's
                     * `span.btnContent` around `span.iconButtonIcon`. Rendering a bare button here instead
                     * put the "x" in the wrong place and made it 23px tall against the baseline's 20px.
                     */
                    React.createElement("div", { className: styles.suffixWrapper },
                        React.createElement("div", { className: styles.clearButtonWrapper },
                            React.createElement("div", { className: styles.popoverRoot },
                                React.createElement("div", { className: styles.popoverElement, "data-hook": "popover-element" },
                                    React.createElement("button", { type: "button", "data-part": "clearButton", "data-hook": "clear-button", onFocus: () => setClearButtonFocused(true), onBlur: () => setClearButtonFocused(false), className: classNames(styles.coreBtnRoot, styles.iconButtonRoot, { [styles.focusableFocus]: clearButtonFocused }, styles['skin-line'], styles['ibSkin-line'], styles['ibTheme-none'], styles.clearButton), "aria-label": "Clear", onClick: onClear },
                                        React.createElement("span", { className: styles.btnContent },
                                            React.createElement("span", { className: styles.iconButtonIcon },
                                                React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 20 20", fill: "currentColor", "aria-hidden": "true", focusable: "false" },
                                                    React.createElement("path", { fillRule: "evenodd", d: "M16 4.707 15.293 4 10 9.293 4.707 4 4 4.707 9.293 10 4 15.293l.707.707L10 10.707 15.293 16l.707-.707L10.707 10 16 4.707Z" })))))))))))),
            error && errorMessage && (React.createElement("div", { id: errorMessageId, className: styles.errorWrapperMessage, "data-hook": "errormessagewrapper-message", role: "alert" },
                React.createElement("svg", { className: styles.errorWrapperIcon, viewBox: "0 0 20 20", width: "20", height: "20", fill: "currentColor", "aria-hidden": "true", focusable: "false" },
                    React.createElement("path", { d: "M9.5,3 C13.084,3 16,5.916 16,9.5 C16,13.084 13.084,16 9.5,16 C5.916,16 3,13.084 3,9.5 C3,5.916 5.916,3 9.5,3 Z M9.5,4 C6.467,4 4,6.467 4,9.5 C4,12.533 6.467,15 9.5,15 C12.533,15 15,12.533 15,9.5 C15,6.467 12.533,4 9.5,4 Z M10,11 L10,12 L9,12 L9,11 L10,11 Z M10,7 L10,10 L9,10 L9,7 L10,7 Z", fillRule: "evenodd" })),
                errorMessage)))));
});
//# sourceMappingURL=TextField.js.map